/**
 * Secure API Client
 * Wraps the standard API client with security features
 */

import { apiClient } from '../api/api-client';
import { ApiResponse } from '../../../contracts/types';
import { rateLimiter, RateLimitRequest } from './rate-limiter';
import { cspService } from './csp';
import { auditService } from '../auth/audit-service';

export interface SecurityConfig {
  enableRateLimiting: boolean;
  enableCSPValidation: boolean;
  enableRequestValidation: boolean;
  enableResponseValidation: boolean;
  logSecurityEvents: boolean;
}

export interface SecurityHeaders {
  'X-Content-Type-Options'?: string;
  'X-Frame-Options'?: string;
  'X-XSS-Protection'?: string;
  'Strict-Transport-Security'?: string;
  'Referrer-Policy'?: string;
  'Permissions-Policy'?: string;
  'Content-Security-Policy'?: string;
  'X-RateLimit-Limit'?: string;
  'X-RateLimit-Remaining'?: string;
  'X-RateLimit-Reset'?: string;
}

export interface SecureRequestConfig {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  data?: any;
  params?: Record<string, any>;
  timeout?: number;
  rateLimitRules?: string[];
  skipSecurityChecks?: boolean;
  requireAuth?: boolean;
}

export interface SecurityViolation {
  type: 'rate_limit' | 'csp' | 'validation' | 'authentication' | 'authorization';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  details: Record<string, any>;
  timestamp: string;
  requestInfo: {
    url: string;
    method: string;
    userAgent?: string;
    ip?: string;
    userId?: string;
  };
}

class SecureApiClient {
  private static instance: SecureApiClient;
  private config: SecurityConfig;
  private violations: SecurityViolation[] = [];

  private constructor(config: SecurityConfig) {
    this.config = config;
    this.setupSecurityHeaders();
  }

  static getInstance(config?: SecurityConfig): SecureApiClient {
    if (!SecureApiClient.instance) {
      const defaultConfig: SecurityConfig = {
        enableRateLimiting: true,
        enableCSPValidation: true,
        enableRequestValidation: true,
        enableResponseValidation: true,
        logSecurityEvents: true
      };
      SecureApiClient.instance = new SecureApiClient(config || defaultConfig);
    }
    return SecureApiClient.instance;
  }

  /**
   * Setup default security headers
   */
  private setupSecurityHeaders(): void {
    // Security headers will be added per-request in the secure request method
    // since the ApiClient class doesn't expose direct access to axios headers
    // The headers are managed through the request config instead
  }

  /**
   * Secure request method with all security checks
   */
  async request<T = any>(config: SecureRequestConfig): Promise<ApiResponse<T>> {
    const startTime = Date.now();

    try {
      // Skip security checks if explicitly requested
      if (config.skipSecurityChecks) {
        // Map config to appropriate method
        const method = config.method?.toLowerCase() || 'get';
        switch (method) {
          case 'get':
            return await apiClient.get<T>(config.url || '', config);
          case 'post':
            return await apiClient.post<T>(config.url || '', config.data, config);
          case 'put':
            return await apiClient.put<T>(config.url || '', config.data, config);
          case 'delete':
            return await apiClient.delete<T>(config.url || '', config);
          default:
            throw new Error(`Unsupported method: ${method}`);
        }
      }

      // 1. Rate limiting check
      if (this.config.enableRateLimiting) {
        await this.checkRateLimit(config);
      }

      // 2. Request validation
      if (this.config.enableRequestValidation) {
        this.validateRequest(config);
      }

      // 3. CSP validation for external resources
      if (this.config.enableCSPValidation) {
        this.validateCSP(config);
      }

      // 4. Add security headers
      const secureConfig = this.addSecurityHeaders(config);

      // 5. Make the actual request
      const method = secureConfig.method?.toLowerCase() || 'get';
      let response: T;
      switch (method) {
        case 'get':
          response = await apiClient.get<T>(secureConfig.url || '', secureConfig);
          break;
        case 'post':
          response = await apiClient.post<T>(secureConfig.url || '', secureConfig.data, secureConfig);
          break;
        case 'put':
          response = await apiClient.put<T>(secureConfig.url || '', secureConfig.data, secureConfig);
          break;
        case 'delete':
          response = await apiClient.delete<T>(secureConfig.url || '', secureConfig);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      // 6. Response validation
      if (this.config.enableResponseValidation) {
        this.validateResponse(response);
      }

      // 7. Log successful request
      if (this.config.logSecurityEvents) {
        await this.logSecurityEvent('request_completed', {
          url: config.url,
          method: config.method || 'GET',
          duration: Date.now() - startTime,
          success: true
        });
      }

      return response;

    } catch (error: any) {
      // Log security violation if it's a security-related error
      if (error.type === 'security_violation') {
        this.recordViolation(error);
      }

      // Log failed request
      if (this.config.logSecurityEvents) {
        await this.logSecurityEvent('request_failed', {
          url: config.url,
          method: config.method || 'GET',
          duration: Date.now() - startTime,
          error: error.message,
          success: false
        });
      }

      throw error;
    }
  }

  /**
   * Check rate limiting for the request
   */
  private async checkRateLimit(config: SecureRequestConfig): Promise<void> {
    const request: RateLimitRequest = {
      endpoint: config.url,
      method: config.method || 'GET',
      timestamp: Date.now(),
      userId: this.getCurrentUserId(),
      ip: await this.getClientIP(),
      userAgent: navigator?.userAgent,
      headers: config.headers
    };

    const result = rateLimiter.checkLimit(request, config.rateLimitRules);

    if (!result.allowed) {
      const violation: SecurityViolation = {
        type: 'rate_limit',
        severity: 'high',
        message: `Rate limit exceeded: ${result.rule.name}`,
        details: {
          limit: result.rule.maxRequests,
          window: result.rule.windowMs,
          retryAfter: result.retryAfter
        },
        timestamp: new Date().toISOString(),
        requestInfo: {
          url: config.url,
          method: config.method || 'GET',
          userAgent: request.userAgent,
          ip: request.ip,
          userId: request.userId
        }
      };

      const error = new Error(`Rate limit exceeded: ${result.rule.name}`);
      (error as any).type = 'security_violation';
      (error as any).violation = violation;
      (error as any).status = 429;
      (error as any).retryAfter = result.retryAfter;

      throw error;
    }

    // Add rate limit headers to the request
    if (!config.headers) {
      config.headers = {};
    }

    config.headers['X-RateLimit-Limit'] = result.rule.maxRequests.toString();
    config.headers['X-RateLimit-Remaining'] = result.remaining.toString();
    config.headers['X-RateLimit-Reset'] = Math.ceil(result.resetTime / 1000).toString();
  }

  /**
   * Validate request parameters and data
   */
  private validateRequest(config: SecureRequestConfig): void {
    // URL validation
    if (!this.isValidURL(config.url)) {
      const violation: SecurityViolation = {
        type: 'validation',
        severity: 'high',
        message: 'Invalid URL format',
        details: { url: config.url },
        timestamp: new Date().toISOString(),
        requestInfo: {
          url: config.url,
          method: config.method || 'GET'
        }
      };

      const error = new Error('Invalid URL format');
      (error as any).type = 'security_violation';
      (error as any).violation = violation;
      throw error;
    }

    // Check for potential XSS in parameters
    if (config.params) {
      for (const [key, value] of Object.entries(config.params)) {
        if (typeof value === 'string' && this.containsPotentialXSS(value)) {
          const violation: SecurityViolation = {
            type: 'validation',
            severity: 'critical',
            message: 'Potential XSS detected in parameters',
            details: { parameter: key, value: value.substring(0, 100) },
            timestamp: new Date().toISOString(),
            requestInfo: {
              url: config.url,
              method: config.method || 'GET'
            }
          };

          const error = new Error('Potential XSS detected in parameters');
          (error as any).type = 'security_violation';
          (error as any).violation = violation;
          throw error;
        }
      }
    }

    // Validate request data for potential threats
    if (config.data && typeof config.data === 'object') {
      this.validateRequestData(config.data, config);
    }
  }

  /**
   * Validate request data for security threats
   */
  private validateRequestData(data: any, config: SecureRequestConfig): void {
    const dataString = JSON.stringify(data);

    // Check for SQL injection patterns
    const sqlPatterns = [
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC)\b)/i,
      /(UNION\s+SELECT)/i,
      /(\-\-)|(\#)|(\/\*.*\*\/)/,
      /(\bOR\b\s*\d+\s*=\s*\d+)/i
    ];

    for (const pattern of sqlPatterns) {
      if (pattern.test(dataString)) {
        const violation: SecurityViolation = {
          type: 'validation',
          severity: 'critical',
          message: 'Potential SQL injection detected',
          details: { pattern: pattern.source, data: dataString.substring(0, 200) },
          timestamp: new Date().toISOString(),
          requestInfo: {
            url: config.url,
            method: config.method || 'GET'
          }
        };

        const error = new Error('Potential SQL injection detected');
        (error as any).type = 'security_violation';
        (error as any).violation = violation;
        throw error;
      }
    }

    // Check for script injection
    if (this.containsPotentialXSS(dataString)) {
      const violation: SecurityViolation = {
        type: 'validation',
        severity: 'critical',
        message: 'Potential script injection detected',
        details: { data: dataString.substring(0, 200) },
        timestamp: new Date().toISOString(),
        requestInfo: {
          url: config.url,
          method: config.method || 'GET'
        }
      };

      const error = new Error('Potential script injection detected');
      (error as any).type = 'security_violation';
      (error as any).violation = violation;
      throw error;
    }
  }

  /**
   * Validate CSP compliance for external resources
   */
  private validateCSP(config: SecureRequestConfig): void {
    const url = new URL(config.url, window.location.origin);

    // Check if the request URL is allowed by CSP
    if (!url.origin.includes(window.location.origin)) {
      const environment = import.meta.env.PROD ? 'production' : 'development';
      const isAllowed = cspService.isResourceAllowed('connect-src', url.href, environment);

      if (!isAllowed) {
        const violation: SecurityViolation = {
          type: 'csp',
          severity: 'high',
          message: 'Request blocked by Content Security Policy',
          details: {
            url: url.href,
            directive: 'connect-src',
            origin: url.origin
          },
          timestamp: new Date().toISOString(),
          requestInfo: {
            url: config.url,
            method: config.method || 'GET'
          }
        };

        const error = new Error('Request blocked by Content Security Policy');
        (error as any).type = 'security_violation';
        (error as any).violation = violation;
        throw error;
      }
    }
  }

  /**
   * Add security headers to the request
   */
  private addSecurityHeaders(config: SecureRequestConfig): SecureRequestConfig {
    const secureHeaders: Record<string, string> = {
      'X-Requested-With': 'XMLHttpRequest',
      'X-Client-Version': '1.0.0',
      'X-Security-Level': 'high',
      ...config.headers
    };

    // Add CSRF token if available
    const csrfToken = this.getCSRFToken();
    if (csrfToken) {
      secureHeaders['X-CSRF-Token'] = csrfToken;
    }

    // Add nonce for CSP
    const nonce = cspService.getCurrentNonce();
    if (nonce) {
      secureHeaders['X-CSP-Nonce'] = nonce;
    }

    return {
      ...config,
      headers: secureHeaders
    };
  }

  /**
   * Validate API response
   */
  private validateResponse<T>(response: ApiResponse<T>): void {
    // Check for security headers in response
    const securityHeaders = [
      'X-Content-Type-Options',
      'X-Frame-Options',
      'Strict-Transport-Security'
    ];

    const missingHeaders = securityHeaders.filter(header =>
      !response.headers?.[header] && !response.headers?.[header.toLowerCase()]
    );

    if (missingHeaders.length > 0) {
      this.recordViolation({
        type: 'validation',
        severity: 'medium',
        message: 'Missing security headers in response',
        details: { missingHeaders },
        timestamp: new Date().toISOString(),
        requestInfo: {
          url: response.config?.url || '',
          method: response.config?.method || 'GET'
        }
      });
    }

    // Validate response data for potential threats
    if (response.data && typeof response.data === 'object') {
      this.validateResponseData(response.data);
    }
  }

  /**
   * Validate response data
   */
  private validateResponseData(data: any): void {
    const dataString = JSON.stringify(data);

    // Check for potential XSS in response
    if (this.containsPotentialXSS(dataString)) {
      this.recordViolation({
        type: 'validation',
        severity: 'high',
        message: 'Potential XSS content in response',
        details: { content: dataString.substring(0, 200) },
        timestamp: new Date().toISOString(),
        requestInfo: {
          url: '',
          method: 'RESPONSE'
        }
      });
    }
  }

  /**
   * Utility methods
   */
  private isValidURL(url: string): boolean {
    try {
      new URL(url, window.location.origin);
      return true;
    } catch {
      return false;
    }
  }

  private containsPotentialXSS(input: string): boolean {
    const xssPatterns = [
      /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
      /<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi,
      /javascript:/gi,
      /on\w+\s*=\s*["'][^"']*["']/gi,
      /<img[\s\S]*?onerror[\s\S]*?>/gi,
      /<svg[\s\S]*?onload[\s\S]*?>/gi
    ];

    return xssPatterns.some(pattern => pattern.test(input));
  }

  private getCurrentUserId(): string | undefined {
    // Implementation would get current user ID from auth context
    return localStorage.getItem('userId') || undefined;
  }

  private async getClientIP(): Promise<string | undefined> {
    // In a real implementation, this might get IP from a service
    return undefined;
  }

  private getCSRFToken(): string | undefined {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || undefined;
  }

  private recordViolation(violation: SecurityViolation): void {
    this.violations.push(violation);

    // Keep only last 100 violations
    if (this.violations.length > 100) {
      this.violations = this.violations.slice(-100);
    }

    // Report to server if configured
    if (this.config.logSecurityEvents) {
      this.reportViolation(violation).catch(error => {
        console.error('Failed to report security violation:', error);
      });
    }
  }

  private async reportViolation(violation: SecurityViolation): Promise<void> {
    try {
      await fetch('/api/v1/security/violations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(violation)
      });
    } catch (error) {
      console.error('Failed to report violation to server:', error);
    }
  }

  private async logSecurityEvent(event: string, details: any): Promise<void> {
    try {
      await auditService.logAction({
        action: `security:${event}`,
        resource_type: 'api_request',
        details
      });
    } catch (error) {
      console.error('Failed to log security event:', error);
    }
  }

  /**
   * Public methods for managing security
   */
  getViolations(): SecurityViolation[] {
    return [...this.violations];
  }

  clearViolations(): void {
    this.violations = [];
  }

  getViolationsByType(type: SecurityViolation['type']): SecurityViolation[] {
    return this.violations.filter(v => v.type === type);
  }

  updateConfig(newConfig: Partial<SecurityConfig>): void {
    this.config = { ...this.config, ...newConfig };
    if (newConfig.enableCSPValidation !== undefined) {
      this.setupSecurityHeaders();
    }
  }

  getConfig(): SecurityConfig {
    return { ...this.config };
  }

  // Convenience methods that mirror the standard API client
  async get<T = any>(url: string, config?: Partial<SecureRequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'GET' });
  }

  async post<T = any>(url: string, data?: any, config?: Partial<SecureRequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'POST', data });
  }

  async put<T = any>(url: string, data?: any, config?: Partial<SecureRequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'PUT', data });
  }

  async delete<T = any>(url: string, config?: Partial<SecureRequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'DELETE' });
  }

  async patch<T = any>(url: string, data?: any, config?: Partial<SecureRequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'PATCH', data });
  }
}

// Singleton instance
export const secureApiClient = SecureApiClient.getInstance();

export default secureApiClient;